import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { BloombergLogo, Exit as ExitIcon } from '@bloomberg/media-ui-icons';
import RegUIClient, { RegUIClientLoginResponse } from 'reg-ui-client';

import { defaultMessages, LABEL_KEYS, useTranslateMessage } from '../i18n';
import { Window } from '../utils/interfaces';
import { getFlags, SHOW_SIGNIN_MODAL_PARAM } from '../utils/utils';
import { LogInRegistration, SOCIAL_SITES, SupportedSocialsType } from '..';

import styles from './SignInModal.module.css';

type SignInModalProps = {
  setUserDataResp?: (userDataResponse: RegUIClientLoginResponse) => void;
  countryCode?: string;
  regUIClient?: RegUIClient;
  enableOnboarding?: boolean;
  allowPassword?: boolean;

  signInClicked: boolean;
  setSignInClicked: Dispatch<React.SetStateAction<boolean>>;

  initializeSignInIframe?: () => void;
  // Do not pass true for this prop without consulting subscription growth team.
  forceModalExperience?: boolean;
};

export type FlagsType = {
  showSignInModal: boolean;
};

export const SignInModal = ({
  countryCode,
  regUIClient,
  enableOnboarding = false,
  allowPassword = true,
  signInClicked,
  setSignInClicked,

  initializeSignInIframe,
  forceModalExperience = false,
}: SignInModalProps) => {
  const translateMessage = useTranslateMessage();
  const [userDataResp, setUserDataResp] = useState<RegUIClientLoginResponse>();
  const [isModalEnabled, setIsModalEnabled] = useState<boolean>();

  /* c8 ignore next 5 */
  const isServer = useMemo(() => {
    return (
      typeof window === 'undefined' || typeof window.document === 'undefined'
    );
  }, []);

  const onClose = () => {
    setSignInClicked(false);
  };

  useEffect(() => {
    (async () => {
      let showSignInModal = false;
      let wasDecisionDueToError = false;
      let errorMsg = '';

      const showSignInModalMockValue = new URLSearchParams(
        window.location.search
      ).get(SHOW_SIGNIN_MODAL_PARAM);

      try {
        if (forceModalExperience) {
          showSignInModal = true;
          console.warn('Forcing sign in modal experience');
        } else {
          const flags = await getFlags(
            showSignInModalMockValue
              ? `?${SHOW_SIGNIN_MODAL_PARAM}=${showSignInModalMockValue}`
              : undefined
          );

          showSignInModal = flags.showSignInModal;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching flags, defaulting to false:', error);
        wasDecisionDueToError = true;
        errorMsg = (error as Error).message;
      } finally {
        if (!showSignInModal) {
          initializeSignInIframe?.();
        }
        setIsModalEnabled(showSignInModal);

        /* c8 ignore next 6 */
        (window as Window).newrelic?.addPageAction('SignInModal', {
          modalOrIframe: showSignInModal ? 'modal' : 'iframe',
          wasDecisionMadeDueToError: wasDecisionDueToError,
          errorMessage: errorMsg,
          wasInitializeSignInIframePassedIn: Boolean(initializeSignInIframe),
          isUsingMock: Boolean(showSignInModalMockValue),
          mockValue: showSignInModalMockValue,
          forceModalExperience,
        });
      }
    })();
  }, []);

  /* c8 ignore next 5 */
  useEffect(() => {
    // close the modal if the login was successful
    if (userDataResp && userDataResp.success) {
      onClose();
    }
  }, [userDataResp]);

  /* c8 ignore next 10 */
  if (isServer) {
    // eslint-disable-next-line no-console
    console.error(
      `Please note that SignInModal is a client-side component since it uses the
DOM/document element. That means that you need to ensure the parent server-side
component has mounted before rendering it. See "media-ui-nav/Nav" for an example.`
    );

    return <></>;
  }

  if (isModalEnabled && signInClicked) {
    return createPortal(
      <div
        className={styles.signInModalContainer}
        data-status="show"
        id="modalContainer"
        onClick={onClose}
      >
        <dialog
          open
          className={styles.signInModal}
          data-testid="sign-in-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.header}>
            <button
              aria-label={translateMessage({
                id: LABEL_KEYS['media-ui-login_home'],
                defaultMessage: defaultMessages['media-ui-login_home'],
              })}
              type="button"
              className={styles.homeButton}
            >
              <BloombergLogo />
            </button>
            <button
              aria-label={translateMessage({
                id: LABEL_KEYS['media-ui-login_closeSignIn'],
                defaultMessage: defaultMessages['media-ui-login_closeSignIn'],
              })}
              role="button"
              data-testid="close-sign-in-modal"
              onClick={onClose}
            >
              <ExitIcon />
            </button>
          </div>

          <div className={styles.loginRegistration}>
            <LogInRegistration
              setUserDataResp={setUserDataResp}
              socialLoginsList={
                [
                  SOCIAL_SITES.GOOGLE,
                  SOCIAL_SITES.APPLE,
                  SOCIAL_SITES.TERMINAL,
                ] as SupportedSocialsType[]
              }
              callerAppInfo={{
                triggerEventName: 'UVerify Event',
                applicationCaller: 'BREG',
              }}
              countryCode={countryCode}
              regUIClient={regUIClient}
              allowPassword={allowPassword}
              enableOnboarding={enableOnboarding}
            />
          </div>
        </dialog>
      </div>,
      document.body
    );
  }

  return null;
};
